import * as XLSX from 'xlsx';

export type XlsxColumn<T> = {
  header: string;
  key: keyof T | ((row: T) => any);
  width?: number;
  format?: 'date' | 'currency' | 'number' | 'percent' | 'text';
};

export type XlsxSheet<T> = {
  name: string;
  rows: T[];
  columns: XlsxColumn<T>[];
};

const FORMAT_MAP: Record<string, string> = {
  date: 'dd/mm/yyyy',
  currency: '#,##0.00 "€";[Red]-#,##0.00 "€";"-"',
  number: '#,##0',
  percent: '0.0%',
  text: '@',
};

function getValue<T>(row: T, key: XlsxColumn<T>['key']): any {
  if (typeof key === 'function') return (key as any)(row);
  return (row as any)[key];
}

function buildSheet<T>(rows: T[], columns: XlsxColumn<T>[]): XLSX.WorkSheet {
  const headerRow = columns.map((c) => c.header);
  const dataRows = rows.map((r) =>
    columns.map((c) => {
      const v = getValue(r, c.key);
      if (v == null || v === '') return null;
      if (c.format === 'date' && typeof v === 'string') {
        const d = new Date(v);
        return isNaN(d.getTime()) ? v : d;
      }
      if ((c.format === 'currency' || c.format === 'number' || c.format === 'percent') && typeof v === 'string') {
        const n = parseFloat(v.replace(',', '.'));
        return isNaN(n) ? v : n;
      }
      return v;
    })
  );

  const aoa: any[][] = [headerRow, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Largeurs colonnes
  ws['!cols'] = columns.map((c) => ({
    wch: c.width ?? Math.max(c.header.length + 2, 12),
  }));

  // Auto-filter sur la 1re ligne
  const range = XLSX.utils.decode_range(ws['!ref']!);
  ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: range.s, e: { r: 0, c: range.e.c } }) };

  // Freeze header
  ws['!freeze'] = { xSplit: 0, ySplit: 1 } as any;

  // En-tête : style gras (note: SheetJS Community ne persiste pas tous les styles, mais cellStyles sur PRO).
  // On applique au moins le format de cellule pour les types numériques/date.
  for (let R = 1; R <= range.e.r; R++) {
    columns.forEach((c, C) => {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[cellRef];
      if (!cell) return;
      const fmt = c.format ? FORMAT_MAP[c.format] : undefined;
      if (fmt) {
        cell.z = fmt;
        if (c.format === 'date' && cell.v instanceof Date) cell.t = 'd';
        if (
          (c.format === 'currency' || c.format === 'number' || c.format === 'percent') &&
          typeof cell.v === 'number'
        ) {
          cell.t = 'n';
        }
      }
    });
  }

  // En-tête : marquage bold via 's' (lu seulement par xlsx PRO mais on ajoute pour compatibilité)
  for (let C = 0; C <= range.e.c; C++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
    if (ws[cellRef]) {
      (ws[cellRef] as any).s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '1E40AF' } },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
    }
  }

  return ws;
}

export function exportXlsx<T>(filename: string, rows: T[], columns: XlsxColumn<T>[], sheetName = 'Données') {
  const wb = XLSX.utils.book_new();
  const ws = buildSheet(rows, columns);
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  const finalName = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, finalName);
}

export function exportXlsxMulti(filename: string, sheets: XlsxSheet<any>[]) {
  const wb = XLSX.utils.book_new();
  sheets.forEach((s) => {
    const ws = buildSheet(s.rows, s.columns);
    XLSX.utils.book_append_sheet(wb, ws, s.name.slice(0, 31));
  });
  const finalName = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, finalName);
}
